import clsx from 'clsx'

/**
 * Barra de progresso indeterminada no topo da viewport.
 *
 * Componente próprio, e não variante do `ProgressBar`, de propósito. Aquele
 * modela progresso DETERMINADO de gamificação — variantes por semântica de
 * jornada, track de 10–12px com inset shadow, fill em gradiente com shine,
 * animação de largura — e a spec `gamification-gamified-progress-bars`
 * normatiza cada um desses pontos. Aqui não há valor, não há semântica de
 * jornada e a caixa é fina e ancorada na viewport: a sobreposição de props é
 * nula.
 *
 * MASTER §7 L5 manda derivar do primitivo irmão antes de criar, e a regra
 * existe para evitar duplicação. Aqui não haveria duplicação — haveria uma
 * spec descrevendo duas coisas diferentes, e toda evolução futura do progresso
 * de jornada perguntando "vale para o modo de boot também?".
 *
 * Substitui dois overlays de tela cheia. O ganho não é estético: overlay
 * comunica "espere", enquanto barra sobre o shell montado comunica "está
 * vindo, e vai ser assim" — e é o overlay que obrigava a pintar uma superfície
 * grande antes de a cor do evento ser conhecida.
 */

export type TopProgressBarProps = {
  /**
   * Sinaliza que o carregamento está demorando mais que o esperado. A cor é
   * redundante com a faixa de mensagem que aparece junto — quem não distingue
   * o matiz lê o texto.
   */
  attention?: boolean
  className?: string
}

export const TopProgressBar = ({
  attention = false,
  className
}: TopProgressBarProps) => (
  <div
    // Progressbar sem `aria-valuenow` é a forma correta de anunciar progresso
    // indeterminado; `aria-busy` no mesmo nó faria o leitor anunciar duas vezes.
    role="progressbar"
    aria-label="Carregando"
    data-testid="top-progress-bar"
    data-attention={attention || undefined}
    className={clsx(
      // `viewport-fit=cover` está no index.html, então em aparelho com notch a
      // borda física fica acima da área segura. Sem o offset a barra some sob
      // o entalhe justamente no aparelho em que este app mais roda.
      'fixed inset-x-0 top-[env(safe-area-inset-top)] z-loader h-0.5 overflow-hidden',
      className
    )}
  >
    <div
      className={clsx(
        'top-progress-bar__fill h-full w-2/5 rounded-full',
        attention
          ? 'bg-[var(--color-status-progress)]'
          : 'bg-[var(--color-brand)]'
      )}
    />
  </div>
)
